import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function NewApplication() {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    number: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
  });
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  const activeStep = 2;
  const steps = [
    { number: 1, title: 'Upload Document', description: 'Attach your application file' },
    { number: 2, title: 'Applicant Information', description: 'Enter contact and address details' },
    { number: 3, title: 'Project Information', description: 'Describe the project and funding needs' },
    { number: 4, title: 'Budget', description: 'Provide budget allocations' },
    { number: 5, title: 'DOL Determination', description: 'Complete final eligibility review' },
  ];

  useEffect(() => {
    const stored = sessionStorage.getItem('newApplicationData');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (!parsed.program) {
          alert('Please select a program from the Dashboard before creating an application.');
          navigate('/dashboard');
          return;
        }
        setUploadedDocument(parsed.document || null);
      } catch {
        sessionStorage.removeItem('newApplicationData');
      }
    } else {
      alert('Please select a program from the Dashboard before creating an application.');
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const downloadDocument = () => {
    if (!uploadedDocument) return;
    const link = document.createElement('a');
    link.href = uploadedDocument.data;
    link.download = uploadedDocument.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const validate = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = 'First name is required.';
    if (!formData.lastName.trim()) nextErrors.lastName = 'Last name is required.';
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Email is invalid.';
    }
    if (!formData.number.trim()) nextErrors.number = 'Number is required.';
    if (!formData.addressLine1.trim()) nextErrors.addressLine1 = 'Address line 1 is required.';
    if (!formData.city.trim()) nextErrors.city = 'City is required.';
    if (!formData.state.trim()) nextErrors.state = 'State is required.';
    if (!formData.country.trim()) nextErrors.country = 'Country is required.';
    if (!formData.zipCode.trim()) nextErrors.zipCode = 'Zip code is required.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleContinue = (event) => {
    event.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    const currentData = JSON.parse(sessionStorage.getItem('newApplicationData') || '{}');
    sessionStorage.setItem('newApplicationData', JSON.stringify({
      ...currentData,
      userInfo: { ...formData },
    }));

    navigate('/dashboard/new-application/project');
  };

  return (
    <div className="page-stack">
        <section className="app-flow-header">
          <div>
            <p className="app-flow-tag">Application Flow</p>
            <h1>Grant Application Details</h1>
            <p className="app-flow-description">
              Finalize your application by completing the contact and address details below.
            </p>
          </div>
          <div className="step-badges">
            {steps.map((step) => (
              <div key={step.number} className={`step-item ${activeStep === step.number ? 'active' : ''}`}>
                <div className="step-number">{step.number}</div>
                <div className="step-text">
                  <div className="step-title">{step.title}</div>
                  <div className="step-desc">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="application-content">
          <div className="form-card">
            <h2 className="section-heading">Step 2: Applicant Information</h2>
            <p className="form-description">
              Complete the details below and submit your application once everything is ready.
            </p>

            {uploadedDocument ? (
              <div className="document-summary">
                <div className="document-summary-header">
                  Uploaded document is ready and will be included with this application.
                </div>
                <div className="document-preview-row">
                  <span>{uploadedDocument.name}</span>
                  <button type="button" className="link-btn" onClick={downloadDocument}>
                    Download File
                  </button>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => navigate('/dashboard/new-application/documents')}
                  >
                    Change Document
                  </button>
                </div>
              </div>
            ) : (
              <div className="document-summary document-warning">
                No document uploaded yet. Please upload your application document first.
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate('/dashboard/new-application/documents')}
                >
                  Upload Document
                </button>
              </div>
            )}

            <form onSubmit={handleContinue} noValidate>
              <div className="form-row">
                <label className="form-field">
                  <span>First Name</span>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? 'input-error' : ''}
                    placeholder="First name"
                  />
                  {errors.firstName && <div className="field-error">{errors.firstName}</div>}
                </label>

                <label className="form-field">
                  <span>Middle Name</span>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    placeholder="Middle name"
                  />
                </label>

                <label className="form-field">
                  <span>Last Name</span>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? 'input-error' : ''}
                    placeholder="Last name"
                  />
                  {errors.lastName && <div className="field-error">{errors.lastName}</div>}
                </label>
              </div>

              <div className="form-row">
                <label className="form-field">
                  <span>Email</span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'input-error' : ''}
                    placeholder="applicant@example.com"
                  />
                  {errors.email && <div className="field-error">{errors.email}</div>}
                </label>

                <label className="form-field">
                  <span>Phone Number</span>
                  <input
                    type="text"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    className={errors.number ? 'input-error' : ''}
                    placeholder="(555) 123-4567"
                  />
                  {errors.number && <div className="field-error">{errors.number}</div>}
                </label>
              </div>

              <label className="form-field">
                <span>Address line 1</span>
                <input
                  type="text"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  className={errors.addressLine1 ? 'input-error' : ''}
                  placeholder="Street address or P.O. box"
                />
                {errors.addressLine1 && <div className="field-error">{errors.addressLine1}</div>}
              </label>

              <label className="form-field">
                <span>Address line 2</span>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Apartment, suite, unit, etc."
                />
              </label>

              <div className="form-row">
                <label className="form-field">
                  <span>City</span>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={errors.city ? 'input-error' : ''}
                    placeholder="City"
                  />
                  {errors.city && <div className="field-error">{errors.city}</div>}
                </label>

                <label className="form-field">
                  <span>State / Province</span>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={errors.state ? 'input-error' : ''}
                    placeholder="State / Province"
                  />
                  {errors.state && <div className="field-error">{errors.state}</div>}
                </label>
              </div>

              <div className="form-row">
                <label className="form-field">
                  <span>Country</span>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={errors.country ? 'input-error' : ''}
                    placeholder="Country"
                  />
                  {errors.country && <div className="field-error">{errors.country}</div>}
                </label>

                <label className="form-field">
                  <span>Zip / Postal Code</span>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className={errors.zipCode ? 'input-error' : ''}
                    placeholder="Zip or postal code"
                  />
                  {errors.zipCode && <div className="field-error">{errors.zipCode}</div>}
                </label>
              </div>

              {submitError && <div className="section-error">{submitError}</div>}

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => navigate('/dashboard')}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Save and continue to Project Info
                </button>
              </div>
            </form>
          </div>
        </section>
    </div>
  );
}

export default NewApplication;
